import React, { useState, useEffect } from "react";
import axios from "axios";
import { API, colors } from "../shared/constants";
import { styles } from "../shared/styles";
import { TabBar } from "../shared/TabBar";
import { BackButton } from "../shared/BackButton";
import { SiteDetail } from "../sites/SiteDetail";
import { SiteFormModal } from "../sites/SiteFormModal";
import { AccountFormModal } from "./AccountFormModal";

export function AccountDetail({ accountId, onBack }) {
  const [account, setAccount] = useState(null);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("general");
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddSite, setShowAddSite] = useState(false);
  const [selectedSite, setSelectedSite] = useState(null);
  const loadAccount = () => {
    Promise.all([
      axios.get(`${API}/accounts/${accountId}/`),
      axios.get(`${API}/sites/`),
    ])
      .then(([accRes, sitesRes]) => {
        setAccount(accRes.data);
        setSites(sitesRes.data.filter((s) => s.account === accountId));
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };
  useEffect(() => {
    loadAccount();
  }, [accountId]);
  if (selectedSite)
    return (
      <SiteDetail
        siteId={selectedSite}
        accountId={accountId}
        onBack={() => setSelectedSite(null)}
      />
    );
  if (loading) return <div style={styles.loading}>Loading...</div>;
  if (!account) return <div style={styles.loading}>Account not found.</div>;
  const tabs = ["general", "contacts", "sites"];
  const tabLabels = {
    general: "General",
    contacts: "Account Contacts",
    sites: "Sites",
  };
  return (
    <div>
      <div style={styles.topbar}>
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <BackButton onClick={onBack} />
          <div style={styles.pageTitle}>{account.account_name}</div>
        </div>
      </div>
      <TabBar
        tabs={tabs}
        tabLabels={tabLabels}
        activeTab={activeTab}
        onSelect={setActiveTab}
      />
      <div style={styles.content}>
        {activeTab === "general" && (
          <div style={styles.card}>
            <div
              style={{
                ...styles.tableHeader,
                gridTemplateColumns: "2fr 1fr 1fr 1fr 80px",
              }}
            >
              <div>Account Name</div>
              <div>Tier</div>
              <div>Status</div>
              <div>Annual Value</div>
              <div></div>
            </div>
            <div
              style={{
                ...styles.tableRow(false),
                gridTemplateColumns: "2fr 1fr 1fr 1fr 80px",
              }}
            >
              <div style={{ fontWeight: "500" }}>{account.account_name}</div>
              <div>{account.tier.toUpperCase()}</div>
              <div>
                <span style={styles.statusBadge(account.status)}>
                  {account.status.charAt(0).toUpperCase() +
                    account.status.slice(1)}
                </span>
              </div>
              <div style={{ fontWeight: "700", color: colors.green }}>
                {account.annual_value > 0
                  ? `$${parseFloat(account.annual_value).toLocaleString("en-AU", { minimumFractionDigits: 2 })}`
                  : "—"}
              </div>
              <div>
                <button
                  onClick={() => setShowEditModal(true)}
                  style={{
                    backgroundColor: colors.blue,
                    color: colors.white,
                    border: "none",
                    padding: "4px 14px",
                    borderRadius: "12px",
                    fontSize: "12px",
                    fontWeight: "600",
                    cursor: "pointer",
                  }}
                >
                  Edit
                </button>
              </div>
            </div>
          </div>
        )}
        {activeTab === "contacts" && (
          <div style={styles.card}>
            {account.contacts.length === 0 ? (
              <div style={styles.loading}>No contacts on file.</div>
            ) : (
              <>
                <div
                  style={{
                    ...styles.tableHeader,
                    gridTemplateColumns: "2fr 1.5fr 1.5fr 2fr 1fr",
                  }}
                >
                  <div>Name</div>
                  <div>Role</div>
                  <div>Phone / Mobile</div>
                  <div>Email</div>
                  <div>Reports</div>
                </div>
                {account.contacts.map((contact) => (
                  <div
                    key={contact.id}
                    style={{
                      ...styles.tableRow(false),
                      gridTemplateColumns: "2fr 1.5fr 1.5fr 2fr 1fr",
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: "500" }}>{contact.name}</div>
                      {contact.position && (
                        <div style={{ fontSize: "12px", color: "#999" }}>
                          {contact.position}
                        </div>
                      )}
                    </div>
                    <div>
                      {contact.role.charAt(0).toUpperCase() +
                        contact.role.slice(1)}
                    </div>
                    <div>
                      {contact.phone && <div>{contact.phone}</div>}
                      {contact.mobile && (
                        <div style={{ fontSize: "12px", color: "#999" }}>
                          {contact.mobile}
                        </div>
                      )}
                    </div>
                    <div style={{ fontSize: "13px" }}>{contact.email}</div>
                    <div>
                      {contact.receives_reports ? (
                        <span style={{ color: "#27ae60", fontWeight: "600" }}>
                          Yes
                        </span>
                      ) : (
                        <span style={{ color: "#999" }}>No</span>
                      )}
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        )}
        {activeTab === "sites" && (
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginBottom: "12px",
              }}
            >
              <button
                style={styles.addButton}
                onClick={() => setShowAddSite(true)}
              >
                + Add Site
              </button>
            </div>
            <div style={styles.card}>
              {sites.length === 0 ? (
                <div style={styles.loading}>No sites for this account.</div>
              ) : (
                <>
                  <div
                    style={{
                      ...styles.tableHeader,
                      gridTemplateColumns: "2fr 2fr 1fr",
                    }}
                  >
                    <div>Site Name</div>
                    <div>Address</div>
                    <div>Status</div>
                  </div>
                  {sites.map((site) => (
                    <div
                      key={site.id}
                      style={{
                        ...styles.tableRow(false),
                        gridTemplateColumns: "2fr 2fr 1fr",
                      }}
                    >
                      <div
                        style={{
                          fontWeight: "500",
                          color: colors.text,
                          cursor: "pointer",
                        }}
                        onClick={() => setSelectedSite(site.id)}
                      >
                        {site.site_name}
                      </div>
                      <div style={{ fontSize: "13px", color: "#666" }}>
                        {site.site_address}
                      </div>
                      <div>
                        <span style={styles.statusBadge(site.status)}>
                          {site.status.charAt(0).toUpperCase() +
                            site.status.slice(1)}
                        </span>
                      </div>
                    </div>
                  ))}
                  <div style={styles.count}>
                    {sites.length} Site{sites.length !== 1 ? "s" : ""}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
      {showEditModal && (
        <AccountFormModal
          existingAccount={account}
          onClose={() => setShowEditModal(false)}
          onSave={() => {
            setLoading(true);
            loadAccount();
          }}
        />
      )}
      {showAddSite && (
        <SiteFormModal
          accountId={accountId}
          onClose={() => setShowAddSite(false)}
          onSave={() => {
            loadAccount();
            setActiveTab("sites");
          }}
        />
      )}
    </div>
  );
}
